import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import {
  FilterPopoverTrigger,
  type FilterPopoverTriggerVisualProps,
} from '@/components/ui/filter-popover-trigger';
import { cn } from '@/utils/classUtils';
import { startOfDay } from 'date-fns';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import {
  boundsToIso,
  formatCustomSummary,
  getPresetRows,
  presetSummary,
  type LastUpdatedPreset,
} from './datePresetBounds';

type UpdatesDatePresetFilterProps = {
  label: string;
  committedPreset: LastUpdatedPreset;
  committedFrom: string;
  committedTo: string;
  onApply: (next: {
    preset: LastUpdatedPreset;
    from?: string;
    to?: string;
  }) => void;
  /** Clears committed filter (URL) — used by trigger clear button */
  onClearCommitted: () => void;
  size?: 'sm';
  filterPopoverProps?: FilterPopoverTriggerVisualProps;
};

export const UpdatesDatePresetFilter: FC<UpdatesDatePresetFilterProps> = ({
  label,
  committedPreset,
  committedFrom,
  committedTo,
  onApply,
  onClearCommitted,
  size,
  filterPopoverProps,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<'list' | 'custom'>('list');

  const [draftPreset, setDraftPreset] =
    useState<LastUpdatedPreset>(committedPreset);
  const [draftFrom, setDraftFrom] = useState(committedFrom);
  const [draftTo, setDraftTo] = useState(committedTo);
  const [customRange, setCustomRange] = useState<DateRange | undefined>(
    undefined
  );

  const syncDraftFromCommitted = useCallback(() => {
    setDraftPreset(committedPreset);
    setDraftFrom(committedFrom);
    setDraftTo(committedTo);
    setPanel('list');
    if (committedPreset === 'custom' && committedFrom && committedTo) {
      const fromD = new Date(committedFrom);
      const toD = new Date(committedTo);
      if (!Number.isNaN(fromD.getTime()) && !Number.isNaN(toD.getTime())) {
        setCustomRange({
          from: startOfDay(fromD),
          to: startOfDay(toD),
        });
      } else {
        setCustomRange(undefined);
      }
    } else {
      setCustomRange(undefined);
    }
  }, [committedPreset, committedFrom, committedTo]);

  useEffect(() => {
    if (menuOpen) {
      syncDraftFromCommitted();
    }
  }, [menuOpen, syncDraftFromCommitted]);

  const isCommittedActive =
    committedPreset !== 'none' &&
    (committedPreset !== 'custom' || Boolean(committedFrom && committedTo));

  const activeSummary = useMemo(() => {
    if (!isCommittedActive) return undefined;
    if (committedPreset === 'custom' && committedFrom && committedTo) {
      return formatCustomSummary(committedFrom, committedTo);
    }
    if (committedPreset === 'custom') {
      return 'Custom range';
    }
    return presetSummary(committedPreset, new Date());
  }, [isCommittedActive, committedPreset, committedFrom, committedTo]);

  const draftHasSelection = useMemo(() => {
    if (draftPreset === 'none') return false;
    if (draftPreset === 'custom') {
      return Boolean(draftFrom && draftTo);
    }
    return true;
  }, [draftPreset, draftFrom, draftTo]);

  const presetRows = getPresetRows(new Date());

  const handleOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setPanel('list');
    }
  }, []);

  const clearDraft = useCallback(() => {
    setDraftPreset('none');
    setDraftFrom('');
    setDraftTo('');
    setCustomRange(undefined);
    setPanel('list');
  }, []);

  const handleApply = useCallback(() => {
    if (!draftHasSelection) return;
    if (draftPreset === 'custom') {
      if (!draftFrom || !draftTo) return;
      onApply({ preset: 'custom', from: draftFrom, to: draftTo });
    } else if (draftPreset !== 'none') {
      onApply({ preset: draftPreset });
    }
    setMenuOpen(false);
    setPanel('list');
  }, [draftHasSelection, draftPreset, draftFrom, draftTo, onApply]);

  const handleCancel = useCallback(() => {
    syncDraftFromCommitted();
    setMenuOpen(false);
    setPanel('list');
  }, [syncDraftFromCommitted]);

  const openCustomPanel = useCallback(() => {
    if (draftPreset === 'custom' && draftFrom && draftTo) {
      const fromD = new Date(draftFrom);
      const toD = new Date(draftTo);
      if (!Number.isNaN(fromD.getTime()) && !Number.isNaN(toD.getTime())) {
        setCustomRange({
          from: startOfDay(fromD),
          to: startOfDay(toD),
        });
      } else {
        setCustomRange(undefined);
      }
    } else {
      setCustomRange(undefined);
    }
    setPanel('custom');
  }, [draftPreset, draftFrom, draftTo]);

  const optionRowClass =
    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:bg-accent';

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isCommittedActive}
      operator={isCommittedActive ? 'between' : undefined}
      activeContent={activeSummary}
      onClear={
        isCommittedActive
          ? () => {
              onClearCommitted();
              setMenuOpen(false);
              setPanel('list');
            }
          : undefined
      }
      open={menuOpen}
      onOpenChange={handleOpenChange}
      size={size}
      {...filterPopoverProps}
    >
      <div className="flex flex-col">
        {panel === 'list' ? (
          <>
            <div className="py-1 min-w-[220px]">
              {presetRows.map(p => {
                const selected = draftPreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={optionRowClass}
                    onClick={() => {
                      setDraftPreset(p.id);
                      setDraftFrom('');
                      setDraftTo('');
                    }}
                  >
                    <span className="truncate text-left">{p.label}</span>
                    {selected ? (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    ) : (
                      <span className="h-4 w-4 shrink-0" />
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                className={cn(optionRowClass, 'text-left')}
                onClick={openCustomPanel}
              >
                <span className="truncate">Custom date range</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground"
                disabled={!draftHasSelection}
                onClick={clearDraft}
              >
                Clear all
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8"
                  disabled={!draftHasSelection}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setPanel('list')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors border-b"
            >
              <ChevronLeft className="size-4" />
              Back
            </button>
            <Calendar
              mode="range"
              selected={customRange}
              onSelect={range => {
                const prevFrom = customRange?.from;
                setCustomRange(range);
                if (
                  range?.from &&
                  range?.to &&
                  prevFrom &&
                  range.from.getTime() === prevFrom.getTime()
                ) {
                  let from = range.from;
                  let to = range.to;
                  if (from > to) {
                    const t = from;
                    from = to;
                    to = t;
                  }
                  const iso = boundsToIso(from, to);
                  setDraftPreset('custom');
                  setDraftFrom(iso.from);
                  setDraftTo(iso.to);
                }
              }}
              numberOfMonths={1}
              defaultMonth={customRange?.from ?? new Date()}
              initialFocus
            />
            <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground"
                disabled={!draftHasSelection}
                onClick={clearDraft}
              >
                Clear all
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8"
                  disabled={!draftHasSelection}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </FilterPopoverTrigger>
  );
};
